import { MuiThemeProvider, Theme, CssBaseline, makeStyles, createStyles, AppBar, Toolbar, IconButton, Dialog, DialogContent, TextField, InputAdornment, Checkbox, CheckboxProps } from "@material-ui/core";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { addMonths, addYears, differenceInDays, endOfMonth, endOfYear, format, startOfMonth, startOfYear } from 'date-fns';
import clsx from 'clsx';
import { chain, chunk, groupBy, toArray } from "lodash";
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PieChart from "./PieChart";
import BarChart from "./BarChart";
import GroupedBarChart from "./GroupedBarChart";
import BarChartWithTrendLine from "./BarChartWithTrendLine";
import { RootState } from "../../store/rootReducer";
import Navbar from "../shared/Navbar";
import themeDashboard from "../shared/themeDashboard";
import { consts } from "../../common/consts";
import DateFnsUtils from "@date-io/date-fns";
import { CalendarIcon } from "../../assets/iconComponents/Icons";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { showNavbar } from "../../store/modules/appStateSlice";
import { useHistory } from "react-router-dom";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import api from "../../store/api";
import { IReasonForDelayData, ITransportByWardData, ITopFiveDestinationsData, ITransportByDayOfWeek, ITransportTimeByDayData, ITransFersPerformedData } from "../../common/types";
import Paper from "@material-ui/core/Paper";
import debounce from 'lodash/debounce';
import eventNames from "../../store/signalr/eventNames";
import { addTaskForEvent, removeTaskForEvent } from "../../store/signalr/tasksForEvents";
import { filterLocationForPopup, getLocationsDataForPopup } from "../popup/hospitalPopupHelpers"
import HospitalPopup from "../popup/HospitalPopup";
import ISelectItem from "../popup/types/ISelectItem";

interface ITransferStats {
    model: {
        recordCount: number,
        recordCountDaily: number,
        recordCountPerHour: number,
        avgKMPerTransfer: number,
        percentOfMultiload: number,
        transferPerDayYellowAlert: number | null,
        transferPerDayRedAlert: number | null,
        transferPerHourYellowAlert: number | null,
        transferPerHourRedAlert: number | null,
        transferDistanceYellowAlert: number | null,
        transferDistanceRedAlert: number | null,
        multiLoadTripsYellowAlert: number | null,
        multiLoadTripsRedAlert: number | null,
    }
}

interface IUserData {
    givenName: string,
    surName: string,
    email: string,
    role: string,
    IsAdmin: boolean,
    exp: number,
    location: string,
    ward: string
}

interface IAuthState {
    isAuthenticated?: boolean,
    token: string | null,
    userName: string | null,
    data: IUserData | null,
    sessionExpired: boolean,
    vehicleSessionId: string | null,
    vehicleNumber: string | null
}

interface IUserType {
    IsAdmin: boolean | false,
    SMADriver: boolean | false,
    CanViewDashboard: boolean | false,
    CanViewDashboardAsSMA: boolean | false
    CanViewSchedule: boolean | false
}
interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}
function NavRightMenu(props: any): JSX.Element {
    return (
        <Button
            onClick={props.rightButtonClick}
            endIcon={<KeyboardArrowDownIcon />}
            style={{ color: "#FFF" }}
        >
            {props.userName}
        </Button>
    );
}

const useStyles = makeStyles((theme) => ({
    yellowBoxText: {
        background: "#F4B827", padding: "3px 16px", fontSize: 14, fontWeight: 700, color: "#03090E", borderRadius: 4, marginLeft: 5, cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    }
}));


const useStylesSubHeader = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1
        },
        menuButton: {
            marginLeft: theme.spacing(1)
        },
        centerText: {
            color: "#FFF",
            fontSize: 18,
            fontWeight: 700,
            opacity: 0.36,
            textTransform: "none"
        },
        centerButton: {
            minWidth: 0,
            padding: "5px 15px"
        },
        datePickerRoot: {
            width: 176,
            padding: "7px 0px",
            height: 38,
        },
        datePickerIcon: {
            backgroundColor: "#02489D",
            borderRadius: 4,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            padding: 6,
            '&:hover': {
                backgroundColor: '#02489D7F'
            },
        }
    })
);

const YellowBox = ({ title, onClick = null, style = {} }: any) => {
    const classes = useStyles();
    if (!title) return null;
    return (
        <span onClick={onClick} className={classes.yellowBoxText} style={{ ...style }}>
            {title}
        </span>
    )
}

const getRoles = (authStore: IAuthState): IUserType => {
    const roles: IUserType = {
        IsAdmin: authStore?.data?.IsAdmin || false,
        SMADriver: authStore?.data ? authStore?.data?.role?.indexOf(consts.Permissions.SMADriver) >= 0 ? true : false : false,
        CanViewDashboard: authStore?.data?.role?.split(',')?.some(p => p === consts.Permissions.ViewDashboard) ?? false,
        CanViewDashboardAsSMA: authStore?.data?.role?.split(',')?.some(p => p === consts.Permissions.SMAAdminPatientTransportDashboard) ?? false,
        CanViewSchedule: authStore?.data?.role?.split(',')?.some(p => p === consts.Permissions.ViewSchedule || p === consts.Permissions.SMAViewSchedule) ?? false,
    }
    return roles;
}

function SubHeader({ leftButtonClick, showArrow = true, title = "All Hospitals", activeOption = "last30days", setActiveOption, authStore }: any) {

    const classes = useStylesSubHeader();
    const history = useHistory();
    const userRoles = getRoles(authStore);
    const active = { color: "#F4B827", opacity: 1 };

    return (
        <div className={classes.root}>
            <AppBar position="static" style={{ backgroundColor: "#01224A" }}>
                <Toolbar variant="dense" style={{ justifyContent: "space-between" }}>
                    <span
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Button className={classes.centerButton} onClick={() => setActiveOption("last30days")} >
                            <Typography className={classes.centerText} style={activeOption == "last30days" ? active : {}}>Last 30 Days</Typography>
                        </Button>
                        <Button className={classes.centerButton} onClick={() => setActiveOption("thismonth")} >
                            <Typography className={classes.centerText} style={activeOption == "thismonth" ? active : {}}>This Month</Typography>
                        </Button>
                        <Button className={classes.centerButton} onClick={() => setActiveOption("thisyear")} >
                            <Typography className={classes.centerText} style={activeOption == "thisyear" ? active : {}}>This Year</Typography>
                        </Button>
                        <Button className={classes.centerButton} onClick={() => setActiveOption("lastmonth")} >
                            <Typography className={classes.centerText} style={activeOption == "lastmonth" ? active : {}}>Last Month</Typography>
                        </Button>
                        <Button className={classes.centerButton} onClick={() => setActiveOption("lastyear")} >
                            <Typography className={classes.centerText} style={activeOption == "lastyear" ? active : {}}>Last Year</Typography>
                        </Button>
                        <Button className={classes.centerButton} onClick={() => setActiveOption("custom")} >
                            <Typography className={classes.centerText} style={activeOption == "custom" ? active : {}}>Custom</Typography>
                        </Button>
                    </span>

                    {
                        userRoles?.CanViewDashboardAsSMA || userRoles?.IsAdmin ?
                            <span
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center"
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    style={{ color: "#FFF", fontSize: 18, fontWeight: 400 }}
                                >
                                    {title}
                                </Typography>
                                {showArrow && <IconButton
                                    edge="start"
                                    className={classes.menuButton}
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={leftButtonClick}
                                >
                                    <KeyboardArrowDownIcon />
                                </IconButton>}
                            </span>
                            :
                            (userRoles?.CanViewDashboardAsSMA ?
                            <Button style={{ minWidth: 0, padding: 0 }} >
                                < YellowBox title="View/Track Bookings" onClick={() => history.push("/transferSchedule")} />
                            </Button> : null
                            )
                    }

                </Toolbar>
            </AppBar>
        </div>
    );
}

function CustomDateHeader({ setStartDateMain, setEndDateMain }: any) {

    const classes = useStylesSubHeader();

    const [startDate, setStartDate] = React.useState<Date>(addMonths(new Date(), -1));
    const [endDate, setEndDate] = React.useState<Date>(new Date());

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date || new Date());
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date || new Date());
    };

    return (
        <div className={classes.root}>
            <AppBar position="static" style={{ backgroundColor: "#000D1D" }}>
                <Toolbar variant="regular" style={{ height: 72 }} >
                    <span
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Typography
                            className={classes.centerText}
                            style={{ opacity: 1, marginRight: "19px" }}
                        >
                            From
                    </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} >
                            <KeyboardDatePicker
                                margin="normal"
                                inputVariant="outlined"
                                format="dd/MM/yyyy"
                                value={startDate}
                                onChange={handleStartDateChange}
                                KeyboardButtonProps={{
                                    "aria-label": "change date",
                                    classes: {
                                        root: classes.datePickerIcon
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        root: classes.datePickerRoot
                                    }
                                }}
                                style={{ margin: 0 }}
                                keyboardIcon={<CalendarIcon fontSize="small" />}
                            />
                        </MuiPickersUtilsProvider>

                        <Typography
                            className={classes.centerText}
                            style={{ opacity: 1, marginLeft: "45px", marginRight: "16px" }}
                        >
                            To
                    </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardDatePicker
                                margin="normal"
                                inputVariant="outlined"
                                format="dd/MM/yyyy"
                                value={endDate}
                                onChange={handleEndDateChange}
                                KeyboardButtonProps={{
                                    "aria-label": "change date",
                                    classes: {
                                        root: classes.datePickerIcon
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        root: classes.datePickerRoot
                                    }
                                }}
                                style={{ margin: 0 }}
                                keyboardIcon={<CalendarIcon fontSize="small" />}
                            />
                        </MuiPickersUtilsProvider>

                        <Button style={{ minWidth: 57, padding: 0, marginLeft: "37px" }} >
                            < YellowBox title="Go" onClick={() => {
                                setStartDateMain(format(startDate, "yyyy-MM-dd"));
                                setEndDateMain(format(endDate, "yyyy-MM-dd"));
                            }} style={{ padding: "7px 16px" }} />
                        </Button>
                    </span>

                </Toolbar>
            </AppBar>
        </div>
    );
}

const useStylesCheckBox = makeStyles({
    root: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    icon: {
        borderRadius: 3,
        width: 15,
        height: 15,
        backgroundColor: '#ECECEC'
    },
    checkedIcon: {
        backgroundColor: '#ECECEC',
        '&:before': {
            display: 'block',
            width: 15,
            height: 15,
            backgroundImage:
                "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
                " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
                "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%2303090E'/%3E%3C/svg%3E\")",
            content: '""',
        }
    },
});

function StyledCheckbox(props: CheckboxProps) {
    const classes = useStylesCheckBox();

    return (
        <Checkbox
            className={classes.root}
            disableRipple
            color="default"
            checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
            icon={<span className={classes.icon} />}
            inputProps={{ 'aria-label': 'decorative checkbox' }}
            {...props}
        />
    );
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: any) {
    return {
        id: `full-width-tab-${index}`,
        'aria-controls': `full-width-tabpanel-${index}`,
    };
}

const DATE_MAP: any = {
    "last30days": [new Date().setDate(new Date().getDate() - 29), new Date()],
    "thismonth": [startOfMonth(new Date), endOfMonth(new Date())],
    "thisyear": [startOfYear(new Date), endOfYear(new Date())],
    "lastmonth": [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
    "lastyear": [startOfYear(addYears(new Date(), -1)), endOfYear(addYears(new Date(), -1))],
}

const NoData = () => {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}><h2>No data found !</h2></div>
}

const LoadingData = () => {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}><h2>Loading data... </h2></div>
}

const hubMessageHandler = (startDate: any, endDate: any, selectedLocations: any[], refreshCharts: any) =>
{
    const reqData = {
        "locationId": selectedLocations,
        "scheduleStartDate": startDate,
        "scheduleEndDate": endDate
    };
    refreshCharts(reqData);
}

const componentTaskId = "dashboard";

function Dashboard() {

    const { authStore } = useSelector((state: RootState) => state);
    const dispatch = useDispatch();
    const [startDate, setStartDate] = React.useState<any>(format(new Date().setDate(new Date().getDate() - 29), "yyyy-MM-dd"));
    const startDateRef = React.useRef(startDate);
    const [endDate, setEndDate] = React.useState<any>(format(new Date(), "yyyy-MM-dd"));
    const endDateRef = React.useRef(endDate);
    const popState = React.useState(false);
    const locationState = React.useState<ISelectItem[]>([]);
    const [selectedHospital, setSelectedHospital] = React.useState<any>("All Hospitals");
    const [selectedLocations, setSelectedLocations] = React.useState<string[]>([]);
    const selectedLocationsRef = React.useRef(selectedLocations);
    const [totalTransfers, setTotalTransfers] = React.useState(0);
    const [avgTransferPerDay, setAvgTransferPerDay] = React.useState(0);
    const [transferPerDayYellowAlert, setTransferPerDayYellowAlert] = React.useState<number | null>(null);
    const [transferPerDayRedAlert, setTransferPerDayRedAlert] = React.useState<number | null>(null);
    const [transferPerHour, setTransferPerHour] = React.useState(0);
    const [transferPerHourYellowAlert, setTransferPerHourYellowAlert] = React.useState<number | null>(null);
    const [transferPerHourRedAlert, setTransferPerHourRedAlert] = React.useState<number | null>(null);
    const [transferDistance, setTransferDistance] = React.useState(0);
    const [transferDistanceYellowAlert, setTransferDistanceYellowAlert] = React.useState<number | null>(null);
    const [transferDistanceRedAlert, setTransferDistanceRedAlert] = React.useState<number | null>(null);

    const [percentMultiLoad, setPercentMultiLoad] = React.useState(0);
    const [percentMultiLoadYellowAlert, setPercentMultiLoadYellowAlert] = React.useState<number | null>(null);
    const [percentMultiLoadRedAlert, setPercentMultiLoadRedAlert] = React.useState<number | null>(null);
    const [reasonForDelayData, setReasonForDelayData] = React.useState<IReasonForDelayData[]>([]);
    const [transportByWardData, setTransportByWardData] = React.useState<ITransportByWardData[]>([]);
    const [topFiveDestinationsData, setTopFiveDestinationsData] = React.useState<ITopFiveDestinationsData[]>([]);
    // const [transportByDayOfWeek, setTransportByDayOfWeek] = React.useState<ITransportByDayOfWeek[]>([]);
    // const [transportByDayOfWeekLoading, setTransportByDayOfWeekLoading] = React.useState<boolean>(true)
    const [transFersPerformedData, setTransFersPerformedData] = React.useState<ITransFersPerformedData[]>([]);
    const [transportTimeByDayData, setTransportTimeByDayData] = React.useState<ITransportTimeByDayData[] | any[]>([]);
    const [activeOption, setActiveOption] = React.useState<string>("last30days");
    const initialMount = React.useRef(true);
    const userRoles = getRoles(authStore);
    const [transfersPerformedLoading, setTransfersPerformedLoading] = React.useState<boolean>(true)
    const [reasonForDelayLoading, setReasonForDelayLoading] = React.useState<boolean>(true)
    const [transportByWardLoading, setTransportByWardLoading] = React.useState<boolean>(true)
    const [topFiveDestinationsLoading, setTopFiveDestinationsLoading] = React.useState<boolean>(true)
    const [transportTimeByDayLoading, settransportTimeByDayLoading] = React.useState<boolean>(true)
    const [barChartLoading, setBarChartLoading] = React.useState<boolean>(true)
    const [barChartData, setBarChartData] = React.useState([]);
    const [barChartTabIndex, setBarChartTabIndex] = React.useState(0);
    const [transferByAttendingDoctor, setTransferByAttendingDoctor] = React.useState<any[]>([]);
    const [transferByAttendingDoctorLoading, setTransferByAttendingDoctorLoading] = React.useState<boolean>(true);

    const whenNewTransferDo = (transferDetails: any) =>{
        if ((!selectedLocationsRef || selectedLocationsRef.current.length == 0 || selectedLocationsRef.current.filter(l => l === transferDetails.LocationId).length > 0)) {
            hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
        }
    }

    const whenTransferUpdatedDo = (transferDetails: any) =>{
        if (!selectedLocationsRef || selectedLocationsRef.current.length == 0 
            || !transferDetails.PreviousLocationId || selectedLocationsRef.current.filter(l => l === transferDetails.PreviousLocationId).length > 0
            || !transferDetails.NewLocationId || selectedLocationsRef.current.filter(l => l === transferDetails.NewLocationId).length > 0) {
            hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
        }
    }

    const whenTransferScheduleUpdatedDo = (transferDetails: any) =>{
        if (!selectedLocationsRef || selectedLocationsRef.current.length == 0
            || !transferDetails.PreviousLocationId || selectedLocationsRef.current.filter(l => l === transferDetails.PreviousLocationId).length > 0
            || !transferDetails.NewLocationId || selectedLocationsRef.current.filter(l => l === transferDetails.NewLocationId).length > 0) {
            hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
        }
    }
    
    const handleBarChartTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setBarChartTabIndex(newValue);
    };

    const handleBarChartChangeIndex = (index: number) => {
        setBarChartTabIndex(index);
    };

    const filterLocation = () => {
        filterLocationForPopup(locationState, setSelectedHospital, setSelectedLocations, null);
    }

    const getLocationsData = async (): Promise<void> => {
        await getLocationsDataForPopup(locationState, 
            setSelectedHospital, 
            setSelectedLocations,
            selectedLocations, 
            null);
    }

    React.useEffect(() => {
        dispatch(showNavbar(false));
        getLocationsData();
        if (!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA && authStore.data?.location)
            setSelectedLocations([authStore.data?.location]);
    }, [])

    React.useEffect(() => {
        addTaskForEvent(eventNames.newTransfer, componentTaskId, whenNewTransferDo);
        addTaskForEvent(eventNames.transferUpdated, componentTaskId, whenTransferUpdatedDo);
        addTaskForEvent(eventNames.transferScheduleUpdated, componentTaskId, whenTransferScheduleUpdatedDo);
        return () => {
            removeTaskForEvent(eventNames.newTransfer, componentTaskId);
            removeTaskForEvent(eventNames.transferUpdated, componentTaskId);
            removeTaskForEvent(eventNames.transferScheduleUpdated, componentTaskId);
        }
    }, [whenNewTransferDo, whenTransferUpdatedDo, whenTransferScheduleUpdatedDo])

    React.useEffect(() => {
        startDateRef.current = startDate;
        endDateRef.current = endDate;
        selectedLocationsRef.current = selectedLocations;
    })

    React.useEffect(() => {
        const reqData = {
            "locationId": selectedLocations,
            "scheduleStartDate": startDate,
            "scheduleEndDate": endDate
        };
        refreshCharts(reqData);

    }, [startDate, endDate, selectedLocations])

    React.useEffect(() => {
        if (activeOption != "custom") {
            if (initialMount.current) {
                initialMount.current = false;
            } else {
                setStartDate(format(DATE_MAP[activeOption][0], "yyyy-MM-dd"));
                setEndDate(format(DATE_MAP[activeOption][1], "yyyy-MM-dd"));
            }
        }
    }, [activeOption])


    React.useEffect(() => {
        setBarChartLoading(true);
        const reqData = {
            "locationId": selectedLocations,
            "scheduleStartDate": startDate,
            "scheduleEndDate": endDate
        };
        if (barChartTabIndex == 0) {
            api.getTransferPerServiceDayStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 1) {
            api.getTransferPerServiceHourStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 2) {
            api.getTransferPickupDelayedStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 3) {
            api.getTransferDelayedStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 4) {
            api.getTransferMultiLoadStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 5) {
            api.getTransferDistanceStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
    }, [barChartTabIndex])

    const debounceRefreshCharts = React.useMemo(() => debounce(async (reqData: any) : Promise<void> => {
        await refreshCharts(reqData);
    }, 2000), []);

    const refreshCharts = async (reqData: any) : Promise<void> => {
        if (!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA && authStore.data?.location)
            reqData.locationId = [authStore.data?.location];

        api.getReasonsForDelayStats("", reqData).then((res) => {
            setReasonForDelayData(res.model.map(({ name, recordCount }: any) => ({ id: name, label: name, value: recordCount })));
            setReasonForDelayLoading(false);
        }).catch(() => {
            setReasonForDelayData([]);
            setReasonForDelayLoading(false);
        })

        api.getTransferTotalStats("", reqData).then(({ model: { recordCount, recordCountDaily, recordCountPerHour, avgKMPerTransfer,
            percentOfMultiload, transferPerDayYellowAlert, transferPerDayRedAlert, transferPerHourYellowAlert, transferPerHourRedAlert,
            transferDistanceYellowAlert, transferDistanceRedAlert, multiLoadTripsYellowAlert, multiLoadTripsRedAlert } }: ITransferStats) => {
            setAvgTransferPerDay(parseFloat(recordCountDaily.toFixed(1)));
            setTransferPerDayYellowAlert(transferPerDayYellowAlert);
            setTransferPerDayRedAlert(transferPerDayRedAlert);
            setTotalTransfers(recordCount);

            setTransferPerHour(parseFloat(recordCountPerHour ? recordCountPerHour.toFixed(1) : "0"));
            setTransferPerHourYellowAlert(transferPerHourYellowAlert);
            setTransferPerHourRedAlert(transferPerHourRedAlert);

            setTransferDistance(parseFloat(avgKMPerTransfer ? avgKMPerTransfer.toFixed(1) : "0"));
            setTransferDistanceYellowAlert(transferDistanceYellowAlert);
            setTransferDistanceRedAlert(transferDistanceRedAlert);

            setPercentMultiLoad(parseFloat(percentOfMultiload ? percentOfMultiload.toFixed(1) : "0"));
            setPercentMultiLoadYellowAlert(multiLoadTripsYellowAlert);
            setPercentMultiLoadRedAlert(multiLoadTripsRedAlert);

        }).catch(() => {
            setAvgTransferPerDay(0);
        })

        //api.getTransportByWardStats("", reqData).then((res) => {

        api.getTransportsByWardHospital("", reqData).then((res) => {
            setTransportByWardData(res.model.map(({ name, recordCount }: any) => ({
                id: name,
                label: name, value: recordCount
            })))
            setTransportByWardLoading(false)
        }).catch(() => {
            setTransportByWardData([]);
            setTransportByWardLoading(false)
        })

        api.getTopFiveDestinationsStats("", reqData).then((res) => {
            setTopFiveDestinationsData(res.model.map(({ name, recordCount }: any) => ({
                id: name,
                label: name, value: recordCount
            })))
            setTopFiveDestinationsLoading(false)
        }).catch(() => {
            setTopFiveDestinationsData([]);
            setTopFiveDestinationsLoading(false)
        })

        // api.getTransportByDayOfAWeekStats("", reqData).then((res) => {
        //     setTransportByDayOfWeek(res.model.map(({ name, recordCount }: any) => ({
        //         id: name,
        //         label: name, value: recordCount
        //     })))
        //     setTransportByDayOfWeekLoading(false)
        // }).catch(() => {
        //     setTransportByDayOfWeek([]);
        //     setTransportByDayOfWeekLoading(false)
        // })

        api.getTransportTimesByDayOfAWeekStats("", reqData).then((res) => {
            setTransportTimeByDayData(chunk(res.model, res.model.length / 7).map((item: any[]) => {
                return {
                    ...chain(item)
                        .keyBy('description')
                        .mapValues('recordCount')
                        .value(), Weekday: item[0].name
                }
            }))
            settransportTimeByDayLoading(false)
        }).catch(() => {
            setTransportTimeByDayData([]);
            settransportTimeByDayLoading(false)
        })

        api.getTransfersPerformedStats("", reqData).then((res) => {
            const data = res.model.map(({ name, recordCount, pickUpDelayed, transferDelayed, aborted }: ITransFersPerformedData) => ({ date: name, "Total transfers": recordCount, "Pickup time delayed": pickUpDelayed, "Transfers delayed": transferDelayed, Aborted: aborted }));
            setTransFersPerformedData(data);
            setTransfersPerformedLoading(false)
        }).catch(() => {
            setTransFersPerformedData([]);
            setTransfersPerformedLoading(false)
        })

        if (barChartTabIndex == 0) {
            api.getTransferPerServiceDayStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 1) {
            api.getTransferPerServiceHourStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 2) {
            api.getTransferPickupDelayedStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 3) {
            api.getTransferDelayedStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 4) {
            api.getTransferMultiLoadStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }
        else if (barChartTabIndex == 5) {
            api.getTransferDistanceStat("", reqData).then((res) => {
                setBarChartData(res.model);
                setBarChartLoading(false);
            }).catch(() => {
                setBarChartData([]);
                setBarChartLoading(false);
            });
        }

        api.getTransferAttendingDoctorStat("", reqData).then((res) => {
            setTransferByAttendingDoctor(res.model.map(({ name, value }: any) => ({
                id: name,
                label: name, value: value
            })))
            setTransferByAttendingDoctorLoading(false)

        }).catch(() => {
            setTransferByAttendingDoctor([]);
            setTransferByAttendingDoctorLoading(false)
        })
    };

    return (
        <React.Fragment>
            <MuiThemeProvider theme={themeDashboard}>
                <CssBaseline />
                <Navbar />
               
                <SubHeader leftButtonClick={() => popState[1](true)} title={selectedHospital} activeOption={activeOption} setActiveOption={setActiveOption} authStore={authStore} />
                {activeOption == "custom" && <CustomDateHeader setEndDateMain={setEndDate} setStartDateMain={setStartDate} />}
                <Paper style={{ height: "100vh", minWidth: 1024, overflowY: "scroll" }}>
                    <Grid container>
                        <Grid item xs={6} md={4} lg={3} style={{
                          
                            border: "1px solid rgba(255, 255, 255, 0.17)",
                            borderLeft: 0,
                          
                            borderTop: 0,
                            
                        }}>
                            <div
                                style={{
                                    textAlign: "center",
                                  
                                    paddingTop: 60,
                                    paddingBottom: 50
                                }}
                            >
                                <Typography variant="h2">{totalTransfers}</Typography>
                                <Typography variant="h3" style={{ opacity: "50%" }}>
                                    Total Transfers
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={6} md={4} lg={2} style={{
                            border: "1px solid rgba(255, 255, 255, 0.17)",
                          
                           
                            borderTop: 0,
                        }}>
                            <div
                                style={{
                                    textAlign: "center",
                                    paddingTop: 60,
                                    paddingBottom: 50
                                }}
                            >
                                <Typography variant="h2"
                                    style={{
                                        color: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert ? "#E05151" :
                                            ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayYellowAlert !== null && avgTransferPerDay < transferPerDayYellowAlert ? "#F4B827" : "#fff"))
                                    }}>
                                {avgTransferPerDay}</Typography>
                                <Typography variant="h3" style={{
                                    opacity: (((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert) || ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayYellowAlert !== null && avgTransferPerDay < transferPerDayYellowAlert)) ? "100%" : "50%",
                                    maxWidth: "3",
                                    color: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerDayYellowAlert !== null && avgTransferPerDay < transferPerDayYellowAlert ? "#F4B827" : "#fff"))
                                }}>
                                    Transfers Per Day

                                </Typography>
                                { (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && <Typography variant="h3" style={{
                                    
                                    fontWeight: "bold",
                                    width: "fit-content",
                                    padding: 5,
                                    marginTop: 5,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    backgroundColor: (transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert ? "#E05151" :
                                        (transferPerDayYellowAlert !== null && avgTransferPerDay < transferPerDayYellowAlert ? "#F4B827" : "#fff")),
                                    display: (transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert ? "block" :
                                        (transferPerDayYellowAlert !== null && avgTransferPerDay < transferPerDayYellowAlert ? "block" : "none")),
                                    color: "#00152E"
                                }}>
                                    <ReportProblemOutlinedIcon style={{ fontSize: '16px', verticalAlign: "middle" }} /> Below KPI of {(transferPerDayRedAlert !== null && avgTransferPerDay < transferPerDayRedAlert ? transferPerDayRedAlert :
                                        transferPerDayYellowAlert)}
                                </Typography>}
                            </div>
                        </Grid>

                        <Grid item xs={6} md={4} lg={2} style={{
                            border: "1px solid rgba(255, 255, 255, 0.17)",

                           
                            borderTop: 0,
                        }}>
                            <div
                                style={{
                                    textAlign: "center",
                                    
                                    paddingTop: 60,
                                    paddingBottom: 50
                                }}
                            >
                                <Typography variant="h2"
                                    style={{
                                        color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert ? "#E05151" :
                                            ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&  transferPerHourYellowAlert !== null && transferPerHour < transferPerHourYellowAlert ? "#F4B827" : "#fff"))
                                    }}> { transferPerHour }</Typography>
                                <Typography variant="h3" style={{
                                    opacity: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert) || ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin)&&transferPerHourYellowAlert !== null && transferPerHour < transferPerHourYellowAlert) ? "100%" : "50%",
                                    maxWidth: "3",
                                    color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferPerHourYellowAlert !== null && transferPerHour < transferPerHourYellowAlert ? "#F4B827" : "#fff"))
                                    }}>
                                    Transfers Per Hour
                                </Typography>
                               { (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && <Typography variant="h3" style={{
                                  
                                    fontWeight: "bold",
                                    width: "fit-content",
                                    padding: 5,
                                    marginTop: 5,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    backgroundColor: (transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert ? "#E05151" :
                                        (transferPerHourYellowAlert !== null && transferPerHour < transferPerHourYellowAlert ? "#F4B827" : "#fff")),
                                    display: (transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert ? "block" :
                                        (transferPerHourYellowAlert !== null && transferPerHour < transferPerHourYellowAlert ? "block" : "none")),
                                    color: "#00152E"
                                }}>
                                    <ReportProblemOutlinedIcon style={{ fontSize: '16px', verticalAlign: "middle" }} /> Below KPI of {(transferPerHourRedAlert !== null && transferPerHour < transferPerHourRedAlert ? transferPerHourRedAlert :
                                        transferPerHourYellowAlert)}
                                </Typography>}
                            </div>
                        </Grid>
                        <Grid item xs={6} md={6} lg={2} style={{
                            border: "1px solid rgba(255, 255, 255, 0.17)",

                           
                            borderTop: 0,
                        }}>
                            <div
                                style={{
                                    textAlign: "center",
                                   
                                    paddingTop: 60,
                                    paddingBottom: 50
                                }}
                            >
                              
                                <Typography variant="h2" style={{
                                    display: "inline-block",
                                    color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert ? "#F4B827" : "#fff"))
                                }}> {transferDistance}</Typography> <Typography variant="h3" style={{
                                        opacity: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert) || (transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert) ? "100%" : "50%",
                                        maxWidth: "3", display: "inline-block",
                                    color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? "#E05151" :
                                            ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert ? "#F4B827" : "#fff"))}}>km</Typography>
                                <Typography variant="h3" style={{
                                    opacity: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert) || ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert) ? "100%" : "50%",
                                    maxWidth: "3",
                                    color: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert ? "#F4B827" : "#fff"))
                                }}>
                                    Transfers Distance
                                </Typography>
                                { (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && <Typography variant="h3" style={{
                                    
                                    fontWeight: "bold",
                                    width: "fit-content",
                                    padding: 5,
                                    marginTop: 5,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    backgroundColor: (transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? "#E05151" :
                                        (transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert ? "#F4B827" : "#fff")),
                                    display: (transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? "block" :
                                        (transferDistanceYellowAlert !== null && transferDistance > transferDistanceYellowAlert ? "block" : "none")),
                                    color: "#00152E"
                                }}>
                                    <ReportProblemOutlinedIcon style={{ fontSize: '16px', verticalAlign: "middle" }} /> Above KPI of {(transferDistanceRedAlert !== null && transferDistance > transferDistanceRedAlert ? transferDistanceRedAlert :
                                        transferDistanceYellowAlert)}
                                </Typography>}
                            </div>
                        </Grid>
                        <Grid item xs={6} md={6} lg={3} style={{
                            border: "1px solid rgba(255, 255, 255, 0.17)",
                            borderRight: 0,
                           
                            borderTop: 0,
                        }}> 
                            <div
                                style={{
                                    textAlign: "center",
                                    
                                    paddingTop: 60,
                                    paddingBottom: 50
                                }}
                            >
                                <Typography variant="h2" style={{
                                    display: "inline-block",
                                    color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert ? "#F4B827" : "#fff"))
                                }}>{percentMultiLoad}</Typography> <Typography variant="h3" style={{
                                    opacity: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert) || (percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert) ? "100%" : "50%",
                                    maxWidth: "3", display: "inline-block",
                                    color: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) &&percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert ? "#F4B827" : "#fff"))
                                    }}>%</Typography>
                                <Typography variant="h3" style={{
                                    opacity: ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert) || ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert) ? "100%" : "50%",
                                    maxWidth: "3",
                                    color: ( (userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? "#E05151" :
                                        ((userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert ? "#F4B827" : "#fff"))
                                }}>
                                    Multiload Trip
                                </Typography>
                               {(userRoles.CanViewDashboardAsSMA || userRoles.IsAdmin) && <Typography variant="h3" style={{
                                  
                                    fontWeight: "bold",
                                    width: "fit-content",
                                    padding: 5,
                                    marginTop: 5,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    backgroundColor: (percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? "#E05151" :
                                        (percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert ? "#F4B827" : "#fff")),
                                    display: (percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? "block" :
                                        (percentMultiLoadYellowAlert !== null && percentMultiLoad < percentMultiLoadYellowAlert ? "block" : "none")),
                                    color: "#00152E"
                                }}>
                                    <ReportProblemOutlinedIcon style={{ fontSize: '16px', verticalAlign: "middle" }} /> Below KPI of {(percentMultiLoadRedAlert !== null && percentMultiLoad < percentMultiLoadRedAlert ? percentMultiLoadRedAlert :
                                        percentMultiLoadYellowAlert)}
                                </Typography>}
                            </div>
                        </Grid>

                        {/* Graphs */}

                        <Grid item xs={12} md={12} lg={12}>
                            <div
                                style={{
                                    height: "348px",
                                    borderTop: "1px solid rgba(255, 255, 255, 0.17)",
                                }}
                            >
                                {transfersPerformedLoading ? <LoadingData /> :
                                    <div>
                                        {transFersPerformedData.length > 0 ? <GroupedBarChart data={transFersPerformedData} title="Transfers Performed" colors={consts.GRAPH_COLORS_BAR} /> : <NoData />}
                                    </div>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                            <AppBar position="static" style={{ marginLeft: '3em', marginRight: '3em', width: 'auto', backgroundColor: "transparent", boxShadow: "none" }}>
                                <Tabs value={barChartTabIndex} onChange={handleBarChartTabChange} aria-label="barchart tabs" TabIndicatorProps={{
                                    style: {
                                        display: "none",
                                    }
                                }}>
                                    <Tab label="Transfers Per Day" {...a11yProps(0)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 0 ? 600 : "normal" }} />
                                    <Tab label="Transfer Per Hour" {...a11yProps(1)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 1 ? 600 : "normal" }} />
                                    <Tab label="Transfer With Pickup Delayed" {...a11yProps(2)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 2 ? 600 : "normal" }} />
                                    <Tab label="Transfer With Transport Delayed" {...a11yProps(3)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 3 ? 600 : "normal" }} />
                                    <Tab label="% Multiload" {...a11yProps(4)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 4 ? 600 : "normal" }} />
                                    <Tab label="Transfer Distance (km)" {...a11yProps(5)} style={{ maxWidth: 'none', fontWeight: barChartTabIndex == 5 ? 600 : "normal" }} />
                                    
                                </Tabs>
                            </AppBar>
                            <TabPanel value={barChartTabIndex} index={0}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                            <TabPanel value={barChartTabIndex} index={1}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                            <TabPanel value={barChartTabIndex} index={2}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                            <TabPanel value={barChartTabIndex} index={3}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                            <TabPanel value={barChartTabIndex} index={4}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                            <TabPanel value={barChartTabIndex} index={5}>
                                {barChartLoading ? <LoadingData /> :
                                    <div>
                                        {barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}
                                    </div>
                                }
                            </TabPanel>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div
                                style={{
                                    height: "348px",
                                    border: "1px solid rgba(255, 255, 255, 0.17)",
                                    borderLeft: "0px",
                                }}
                            >
                                {reasonForDelayLoading ? <LoadingData /> :
                                    <div>
                                        {reasonForDelayData.length > 0 ? <PieChart chartData={reasonForDelayData} colors={consts.GRAPH_COLORS} title="Reason For Delay" /> : <NoData />}
                                    </div>}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div
                                style={{
                                    height: "348px",
                                    border: "1px solid rgba(255, 255, 255, 0.17)",
                                    borderLeft: "0px",
                                }}
                            >
                                {transportByWardLoading ? <LoadingData /> : <div>
                                    {transportByWardData.length > 0 ? <PieChart chartData={transportByWardData} colors={consts.GRAPH_COLORS} title={`Transport By ${selectedLocations.length == 1 ? "Ward" : "Hospital"}`} /> : <NoData />}
                                </div>}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div
                                style={{
                                    height: "348px",
                                    border: "1px solid rgba(255, 255, 255, 0.17)",
                                    borderLeft: "0px",
                                }}
                            >
                                {topFiveDestinationsLoading ? <LoadingData /> :
                                    <div>
                                        {topFiveDestinationsData.length > 0 ? <PieChart chartData={topFiveDestinationsData} colors={consts.GRAPH_COLORS} title="Top 5 Destinations" /> : <NoData />}
                                    </div>
                                }
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            {transportTimeByDayLoading ? <LoadingData /> :
                                <div
                                    style={{
                                        height: "348px",
                                        border: "1px solid rgba(255, 255, 255, 0.17)",
                                        borderLeft: "0px",
                                    }}
                                >
                                    {transportTimeByDayData.length > 0 ? <BarChart barChartData={transportTimeByDayData} colors={consts.GRAPH_COLORS_BAR2} title="Transport Times By Day of Week" subtitle={!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA ? "Actual pickup time" : null} /> : <NoData />}
                                </div>
                            }
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div
                                style={{
                                    height: "348px",
                                    border: "1px solid rgba(255, 255, 255, 0.17)",
                                    borderLeft: "0px",
                                }}
                            >
                                {transferByAttendingDoctorLoading ? <LoadingData /> :
                                    <div>
                                        {transferByAttendingDoctor.length > 0 ? <PieChart chartData={transferByAttendingDoctor} colors={consts.GRAPH_COLORS} title="Attending / Referring Doctor" /> : <NoData />}
                                    </div>
                                }
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div
                                style={{
                                    height: "348px",
                                    border: "1px solid rgba(255, 255, 255, 0.17)",
                                    borderLeft: "0px",
                                }}
                            ></div>
                        </Grid>
                    </Grid>
                </Paper>
                <HospitalPopup popState={popState} filterLocation={filterLocation} locationState={locationState} />
            </MuiThemeProvider>
        </React.Fragment>
    );
}

export default Dashboard;